import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, TextField, Switch, Alert,
  useMediaQuery, useTheme,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Lightbulb as TipIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useAuth } from '../context/AuthContext';
import { wellnessTipService, WellnessTip, WellnessTipCreate, WellnessTipUpdate } from '../services/wellnessTipService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { wellnessTipColumns } from '../config/wellnessTipColumns';
import { formatDate, formatDateTime, convertUTCToTimezoneInput, convertTimezoneInputToUTC } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useCrudHandlers } from '../hooks/useCrudHandlers';

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
    handlers: {
      // Quill treats #000000 as the default color (no value attr on the option),
      // so the picker sends '' when black is clicked — which removes the format.
      // Convert '' → explicit rgb(0,0,0) and use formatText (not format) on a
      // real selection so the cursor doesn't retain the color for new typing.
      color(this: any, value: string) {
        const range = this.quill.getSelection();
        const colorValue = value || 'rgb(0, 0, 0)';
        if (range && range.length > 0) {
          this.quill.formatText(range.index, range.length, 'color', colorValue, 'user');
        } else {
          this.quill.format('color', colorValue, 'user');
        }
      },
    },
  },
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'bullet',
  'link',
];

const QUILL_EMPTY = '<p><br></p>';

type FormData = WellnessTipCreate & WellnessTipUpdate;

const WellnessTipPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WellnessTip | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tipValue, setTipValue] = useState('');

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormData>();

  const getSortValue = useCallback((item: WellnessTip, key: string): any => {
    switch (key) {
      case 'fromDate': return item.from_date;
      case 'active': return item.active ? 1 : 0;
      case 'createdAt': return item.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((item: WellnessTip): string[] => [item.tip], []);

  const table = useTablePage<WellnessTip>({
    entityType: 'wellness-tips',
    columns: wellnessTipColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<WellnessTip>({
    table,
    createFn: (data) => wellnessTipService.create(currentUser!.tenant_id!, data),
    updateFn: (id, data) => wellnessTipService.update(id as number, data),
    deleteFn: (id) => wellnessTipService.delete(id as number),
    entityName: 'Wellness Tip',
    onSuccess: () => { setAddDialogOpen(false); setEditingItem(null); reset(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); },
  });

  useEffect(() => {
    if (currentUser?.user_privilege?.wellness) {
      table.loadData(() => wellnessTipService.getAll(currentUser.tenant_id ?? undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const openAdd = () => {
    setEditingItem(null);
    setTipValue('');
    reset({ from_date: '', active: true, tip: '' });
    setAddDialogOpen(true);
  };

  const openEdit = (item: WellnessTip) => {
    setEditingItem(item);
    setTipValue(item.tip);
    reset({
      from_date: convertUTCToTimezoneInput(item.from_date, userTimezone),
      active: item.active,
      tip: item.tip,
    });
    setAddDialogOpen(true);
  };

  const handleEdit = () => { if (table.selectedItem) openEdit(table.selectedItem); table.handleMenuClose(); };
  const handleDelete = () => { if (table.selectedItem) setDeleteDialogOpen(true); table.handleMenuClose(); };
  const onSubmit = (data: FormData) => {
    crud.handleCreateOrUpdate({
      ...data,
      from_date: convertTimezoneInputToUTC(data.from_date as string, userTimezone),
      tip: tipValue === QUILL_EMPTY ? '' : tipValue,
    }, editingItem);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => [
    { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
    { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' },
  ];

  // Strip HTML tags for table preview
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  const getColumnValue = (item: WellnessTip, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'fromDate': return formatDateTime(item.from_date, {}, userTimezone);
      case 'active': return <Chip label={item.active ? 'Yes' : 'No'} color={item.active ? 'success' : 'default'} size="small" />;
      case 'tip':
        return (
          <Typography variant="body2" onClick={(e) => { e.stopPropagation(); openEdit(item); }}
            sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', '&:hover': { color: 'primary.dark' },
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
            {stripHtml(item.tip)}
          </Typography>
        );
      case 'createdAt': return formatDate(item.created_at, userTimezone);
      default: return 'N/A';
    }
  };

  const renderMobileCard = (item: WellnessTip) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="body2" color="text.secondary">{formatDateTime(item.from_date, {}, userTimezone)}</Typography>
        <Chip label={item.active ? 'Active' : 'Inactive'} color={item.active ? 'success' : 'default'} size="small" />
      </Box>
      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {stripHtml(item.tip)}
      </Typography>
    </>
  );

  if (!currentUser?.user_privilege?.wellness) {
    return <Alert severity="error">You don't have permission to access this page.</Alert>;
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading wellness tips:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Wellness Tips"
        searchPlaceholder="Search wellness tips..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
            Add Tip
          </Button>
        }
      />
      {isMobile ? (
        <MobileCardGrid data={table.sortedData} renderCard={renderMobileCard} onMenuClick={table.handleRowClick}
          getItemKey={(i) => i.id} emptyIcon={<TipIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No wellness tips found" />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody data={table.sortedData} {...table.tableBodyProps} getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick} getRowKey={(i) => i.id} />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      <ClinicDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
        title={editingItem ? 'Edit Wellness Tip' : 'Add Wellness Tip'}
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingItem ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
          </>
        }>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>From Date</Typography>
            <TextField {...register('from_date', { required: 'From date is required' })} type="datetime-local" fullWidth size="small"
              error={!!errors.from_date} helperText={errors.from_date?.message as string}
              InputLabelProps={{ shrink: true }} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Active</Typography>
            <Controller name="active" control={control} defaultValue={true}
              render={({ field }) => <Switch checked={!!field.value} onChange={field.onChange} />} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, mb: 1 }}>Tip</Typography>
            <Box className="ql-rtl-wrapper" sx={{
              '& .ql-container': { borderRadius: '0 0 4px 4px' },
              '& .ql-toolbar': { borderRadius: '4px 4px 0 0' },
              '& .ql-editor': { minHeight: 180, maxHeight: 320, overflowY: 'auto', direction: 'rtl', textAlign: 'right' },
            }}>
              <ReactQuill
                value={tipValue}
                onChange={setTipValue}
                modules={quillModules}
                formats={quillFormats}
              />
            </Box>
          </Box>
        </Box>
      </ClinicDialog>

      <ConfirmDeleteDialog open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); table.handleMenuClose(); }}
        onConfirm={() => crud.handleDelete(table.selectedItem)}
        entityType="Wellness Tip" entityName={stripHtml(table.selectedItem?.tip || '').slice(0, 50)} />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default WellnessTipPage;
